import React, { useState, useEffect } from 'react';
import { 
  Card, DatePicker, Button, Space, Typography, 
  Table, Row, Col, Spin, Radio, Modal, Tag, message
} from 'antd';
import { SearchOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import reportService from '../../services/report.service';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const AgentWinLossReport = () => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [quickSelect, setQuickSelect] = useState('thisMonth');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [subAgentDetails, setSubAgentDetails] = useState([]);
  const [playerDetails, setPlayerDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showSubAgentDetails, setShowSubAgentDetails] = useState(false);
  const [showPlayerDetails, setShowPlayerDetails] = useState(false);

  useEffect(() => {
    loadReport();
  }, [dateRange]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await reportService.getAgentWinLoss({
        startDate: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1].format('YYYY-MM-DD HH:mm:ss')
      });
      
      // 修復：確保正確設置 report 數據，處理後端返回的數據結構
      console.log('API Response:', response); // Debug 日誌
      setReport(response);
    } catch (error) {
      console.error('載入代理輸贏報表失敗:', error);
      message.error('載入報表失敗');
      // 設置空的備用數據結構
      setReport({
        report: {
          agentInfo: { agentName: '', rebateRate: 0, commissionRate: 0 },
          subAgentsSummary: {
            betAmount: 0, winLoss: 0, validAmount: 0, rebateRate: 0,
            rebate: 0, totalProfit: 0, commission: 0, upline: 0, uplineValid: 0
          },
          directPlayers: {
            betAmount: 0, winLoss: 0, validAmount: 0, rebateRate: 0,
            rebate: 0, totalProfit: 0, commission: 0, upline: 0, uplineValid: 0
          },
          total: {
            validAmount: 0, winLoss: 0, myProfit: 0, uplineValidAmount: 0, uplineAmount: 0
          }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // 修復：載入下級代理明細，增加錯誤處理
  const loadSubAgentDetails = async () => {
    try {
      setLoadingDetails(true);
      const response = await reportService.getSubAgentsDetail({
        startDate: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1].format('YYYY-MM-DD HH:mm:ss')
      });
      
      console.log('Sub Agent Details Response:', response); // Debug 日誌
      
      if (response && response.data && Array.isArray(response.data)) {
        // 確保每個項目都有唯一的 key
        const dataWithKeys = response.data.map((item, index) => ({
          ...item,
          key: item.agentId || item.id || `sub-agent-${index}`
        }));
        setSubAgentDetails(dataWithKeys);
        setShowSubAgentDetails(true);
        setShowPlayerDetails(false);
      } else {
        console.warn('下級代理明細數據格式異常:', response);
        setSubAgentDetails([]);
        message.warning('沒有找到下級代理明細');
      }
    } catch (error) {
      console.error('載入下級代理明細失敗:', error);
      message.error('載入明細失敗');
      setSubAgentDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  // 修復：載入玩家明細，增加錯誤處理
  const loadPlayerDetails = async () => {
    try {
      setLoadingDetails(true);
      const response = await reportService.getDirectPlayersDetail({
        startDate: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1].format('YYYY-MM-DD HH:mm:ss')
      });
      
      console.log('Player Details Response:', response); // Debug 日誌
      
      if (response && response.data && Array.isArray(response.data)) {
        // 確保每個項目都有唯一的 key
        const dataWithKeys = response.data.map((item, index) => ({
          ...item,
          key: item.playerId || item.userId || item.id || `player-${index}`
        }));
        setPlayerDetails(dataWithKeys);
        setShowPlayerDetails(true);
        setShowSubAgentDetails(false);
      } else {
        console.warn('玩家明細數據格式異常:', response);
        setPlayerDetails([]);
        message.warning('沒有找到玩家明細');
      }
    } catch (error) {
      console.error('載入玩家明細失敗:', error);
      message.error('載入明細失敗');
      setPlayerDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleQuickSelect = (value) => {
    setQuickSelect(value);
    let start, end;
    
    switch(value) {
      case 'today':
        start = dayjs().startOf('day');
        end = dayjs().endOf('day');
        break;
      case 'yesterday':
        start = dayjs().subtract(1, 'day').startOf('day');
        end = dayjs().subtract(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        start = dayjs().startOf('week');
        end = dayjs().endOf('week');
        break;
      case 'lastWeek':
        start = dayjs().subtract(1, 'week').startOf('week');
        end = dayjs().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        start = dayjs().startOf('month');
        end = dayjs().endOf('month');
        break;
      case 'lastMonth':
        start = dayjs().subtract(1, 'month').startOf('month');
        end = dayjs().subtract(1, 'month').endOf('month');
        break;
      default:
        return;
    }
    
    setDateRange([start, end]);
  };

  // ========== 格式化函數區塊 ==========
  
  // 格式化貨幣顯示（加千分位）
  const formatCurrency = (num) => {
    const absNum = Math.abs(num);
    return absNum.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // 格式化數字並加上顏色
  // 【統一顏色邏輯】
  // 負數 = 藍色
  // 正數 = 紅色
  // type = 'cost' 時：固定紅色（成本）
  const formatWithColor = (num, type = 'normal') => {
    const formatted = formatCurrency(Math.abs(num));
    let color = '#000';
    
    if (type === 'cost') {
      color = '#cf1322';
    } else {
      // 負數用藍色，正數用紅色
      color = num < 0 ? '#1890ff' : '#cf1322';
    }
    
    return (
      <span style={{ color, fontWeight: 'bold' }}>
        {num < 0 ? '-' : ''}{formatted}
      </span>
    );
  };
  
  // ========== 格式化函數區塊結束 ==========

  // 下級代理表格欄位（匯總數據）
  const subAgentColumns = [
    {
      title: '投注金額',
      dataIndex: 'betAmount',
      key: 'betAmount',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '輸贏金額',
      dataIndex: 'winLoss',
      key: 'winLoss',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    },
    {
      title: '有效投注',
      dataIndex: 'validAmount',
      key: 'validAmount',
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: '洗碼佣金比',
      dataIndex: 'rebateRate',
      key: 'rebateRate',
      align: 'center',
      render: (val) => `${val || 0}%`
    },
    {
      title: '洗碼佣金',
      dataIndex: 'rebate',
      key: 'rebate',
      align: 'right',
      render: (val) => formatWithColor(val, 'cost')
    },
    {
      title: '總獲利',
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    },
    {
      title: '佔成',
      dataIndex: 'commission',
      key: 'commission',
      align: 'center',
      render: (val) => `${val || 0}%`
    },
    {
      title: '交上線有效投注',
      dataIndex: 'uplineValid',
      key: 'uplineValid',
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: '交上線',
      dataIndex: 'upline',
      key: 'upline',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    }
  ];

  // 直屬玩家表格欄位（匯總數據）
  const playerColumns = [
    {
      title: '投注金額',
      dataIndex: 'betAmount',
      key: 'betAmount',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '輸贏金額',
      dataIndex: 'winLoss',
      key: 'winLoss',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    },
    {
      title: '有效投注',
      dataIndex: 'validAmount',
      key: 'validAmount',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '洗碼佣金比',
      dataIndex: 'rebateRate',
      key: 'rebateRate',
      align: 'center',
      render: (val) => `${val || 0}%`
    },
    {
      title: '洗碼佣金',
      dataIndex: 'rebate',
      key: 'rebate',
      align: 'right',
      render: (val) => formatWithColor(val || 0, 'cost')
    },
    {
      title: '總獲利',
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    },
    {
      title: '佔成',
      dataIndex: 'commission',
      key: 'commission',
      align: 'center',
      render: (val) => `${val || 0}%`
    },
    {
      title: '交上線有效投注',
      dataIndex: 'uplineValid',
      key: 'uplineValid',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '交上線',
      dataIndex: 'upline',
      key: 'upline',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    }
  ];

  // ========== 數據計算區塊開始 ==========
  
  // 修復：增加安全的數據取值函數
  const getSafeValue = (value, defaultValue = 0) => {
    return (value !== null && value !== undefined && !isNaN(value)) ? value : defaultValue;
  };
  
  // 修復：使用安全的數據取值
  const subAgentSummary = {
    betAmount: getSafeValue(report?.report?.subAgentsSummary?.betAmount),
    winLoss: getSafeValue(report?.report?.subAgentsSummary?.winLoss),
    validAmount: getSafeValue(report?.report?.subAgentsSummary?.validAmount),
    rebateRate: getSafeValue(report?.report?.subAgentsSummary?.rebateRate),
    rebate: getSafeValue(report?.report?.subAgentsSummary?.rebate),
    totalProfit: getSafeValue(report?.report?.subAgentsSummary?.totalProfit),
    commission: getSafeValue(report?.report?.subAgentsSummary?.commission),
    upline: getSafeValue(report?.report?.subAgentsSummary?.upline),
    uplineValid: getSafeValue(report?.report?.subAgentsSummary?.uplineValid)
  };
  
  const subAgentTableData = [{
    key: 'sub-agent-summary',
    betAmount: subAgentSummary.betAmount,
    winLoss: subAgentSummary.winLoss,
    validAmount: subAgentSummary.validAmount,
    rebateRate: subAgentSummary.rebateRate,
    rebate: subAgentSummary.rebate,
    totalProfit: subAgentSummary.totalProfit,
    commission: subAgentSummary.commission,
    upline: subAgentSummary.upline,
    uplineValid: subAgentSummary.uplineValid
  }];

  // 修復：直屬玩家匯總數據
  const playerSummary = {
    betAmount: getSafeValue(report?.report?.directPlayers?.betAmount),
    winLoss: getSafeValue(report?.report?.directPlayers?.winLoss),
    validAmount: getSafeValue(report?.report?.directPlayers?.validAmount),
    rebateRate: getSafeValue(report?.report?.directPlayers?.rebateRate),
    rebate: getSafeValue(report?.report?.directPlayers?.rebate),
    totalProfit: getSafeValue(report?.report?.directPlayers?.totalProfit),
    commission: getSafeValue(report?.report?.directPlayers?.commission),
    upline: getSafeValue(report?.report?.directPlayers?.upline),
    uplineValid: getSafeValue(report?.report?.directPlayers?.uplineValid)
  };
  
  const playerTableData = [{
    key: 'player-summary',
    betAmount: playerSummary.betAmount || 0,
    winLoss: playerSummary.winLoss || 0,
    validAmount: playerSummary.validAmount || 0,
    rebateRate: playerSummary.rebateRate || 0,
    rebate: playerSummary.rebate || 0,
    totalProfit: playerSummary.totalProfit || 0,
    commission: playerSummary.commission || 0,
    upline: playerSummary.upline || 0,
    uplineValid: playerSummary.uplineValid || 0
  }];

  // 【計算區塊3：我的獲利計算】
  const calculateMyProfit = () => {
    return report?.report?.total?.myProfit || 0;
  };

  // 【計算區塊4：總有效投注計算】
  const calculateTotalValidAmount = () => {
    return report?.report?.total?.validAmount || 0;
  };

  // 【計算區塊5：總輸贏金額計算】
  const calculateTotalWinLoss = () => {
    return report?.report?.total?.winLoss || 0;
  };
  
  // ========== 數據計算區塊結束 ==========

  // ========== 明細表格欄位定義 ==========
  
  // 下級代理明細表格欄位
  const subAgentDetailColumns = [
    {
      title: '代理名稱',
      dataIndex: 'agentName',
      key: 'agentName',
      fixed: 'left',
      width: 120
    },
    {
      title: '投注金額',
      dataIndex: 'betAmount',
      key: 'betAmount',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '輸贏金額',
      dataIndex: 'winLoss',
      key: 'winLoss',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    },
    {
      title: '有效投注',
      dataIndex: 'validAmount',
      key: 'validAmount',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '洗碼佣金比',
      dataIndex: 'rebateRate',
      key: 'rebateRate',
      align: 'center',
      render: (val) => `${val || 0}%`
    },
    {
      title: '洗碼佣金',
      dataIndex: 'rebate',
      key: 'rebate',
      align: 'right',
      render: (val) => formatWithColor(val || 0, 'cost')
    },
    {
      title: '總獲利',
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    },
    {
      title: '佔成',
      dataIndex: 'commission',
      key: 'commission',
      align: 'center',
      render: (val) => `${val || 0}%`
    },
    {
      title: '交上線有效投注',
      dataIndex: 'uplineValid',
      key: 'uplineValid',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '交上線',
      dataIndex: 'needToPay',
      key: 'needToPay',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    }
  ];

  // 玩家明細表格欄位
  const playerDetailColumns = [
    {
      title: '玩家名稱',
      dataIndex: 'playerName',
      key: 'playerName',
      fixed: 'left',
      width: 120
    },
    {
      title: '投注金額',
      dataIndex: 'betAmount',
      key: 'betAmount',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '輸贏金額',
      dataIndex: 'winLoss',
      key: 'winLoss',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    },
    {
      title: '有效投注',
      dataIndex: 'validAmount',
      key: 'validAmount',
      align: 'right',
      render: (val) => formatCurrency(val || 0)
    },
    {
      title: '洗碼佣金比',
      dataIndex: 'rebateRate',
      key: 'rebateRate',
      align: 'center',
      render: () => '0%'  // 固定顯示0%
    },
    {
      title: '洗碼佣金',
      dataIndex: 'rebate',
      key: 'rebate',
      align: 'right',
      render: () => formatCurrency(0)  // 固定顯示0
    },
    {
      title: '總獲利',
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      align: 'right',
      render: (val) => formatWithColor(val || 0)
    }
  ];

  // ========== 明細表格欄位定義結束 ==========

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // 移動端視圖
  const MobileView = () => (
    <div style={{ padding: '16px' }}>
      <Card>
        <Title level={4}>代理輸贏報表</Title>
        
        {/* 日期選擇 */}
        <Radio.Group
          value={quickSelect}
          onChange={(e) => handleQuickSelect(e.target.value)}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="today">今日</Radio.Button>
          <Radio.Button value="yesterday">昨日</Radio.Button>
          <Radio.Button value="thisWeek">本週</Radio.Button>
          <Radio.Button value="lastWeek">上週</Radio.Button>
          <Radio.Button value="thisMonth">本月</Radio.Button>
          <Radio.Button value="lastMonth">上月</Radio.Button>
        </Radio.Group>
        
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          showTime={{ format: 'HH:mm' }}
          format="YYYY-MM-DD HH:mm"
          size="small"
          style={{ width: '100%', marginBottom: 16 }}
        />
        
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={loadReport}
          block
          style={{ background: '#ff6600', borderColor: '#ff6600', marginBottom: 24 }}
        >
          搜尋
        </Button>

        {/* 下級代理輸贏匯總 */}
        <Card 
          size="small" 
          title="下級代理輸贏匯總"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <div style={{ color: '#999' }}>有效投注</div>
              <div style={{ fontWeight: 'bold' }}>
                {formatCurrency(subAgentSummary.validAmount || 0)}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>輸贏金額</div>
              <div>{formatWithColor(subAgentSummary.winLoss || 0)}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>洗碼佣金</div>
              <div>{formatWithColor(subAgentSummary.rebate || 0, 'cost')}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>總獲利</div>
              <div>{formatWithColor(subAgentSummary.totalProfit || 0)}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>交上線有效投注</div>
              <div style={{ fontWeight: 'bold' }}>
                {formatCurrency(subAgentSummary.uplineValid || 0)}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>交上線</div>
              <div>{formatWithColor(subAgentSummary.upline || 0)}</div>
            </Col>
          </Row>
        </Card>

        {/* 直屬玩家輸贏總合 */}
        <Card 
          size="small" 
          title="直屬玩家輸贏總合"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <div style={{ color: '#999' }}>有效投注</div>
              <div style={{ fontWeight: 'bold' }}>
                {formatCurrency(playerSummary.validAmount || 0)}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>輸贏金額</div>
              <div>{formatWithColor(playerSummary.winLoss || 0)}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>洗碼佣金</div>
              <div>{formatWithColor(playerSummary.rebate || 0, 'cost')}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>總獲利</div>
              <div>{formatWithColor(playerSummary.totalProfit || 0)}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>交上線有效投注</div>
              <div style={{ fontWeight: 'bold' }}>
                {formatCurrency(playerSummary.uplineValid || 0)}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>交上線</div>
              <div>{formatWithColor(playerSummary.upline || 0)}</div>
            </Col>
          </Row>
        </Card>

        {/* 總計卡片 */}
        <Card 
          size="small" 
          title="總計"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <div style={{ color: '#999' }}>有效投注</div>
              <div style={{ fontWeight: 'bold' }}>
                {formatCurrency(calculateTotalValidAmount())}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>輸贏金額</div>
              <div>{formatWithColor(calculateTotalWinLoss())}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>洗碼佣金</div>
              <div>{formatWithColor((subAgentSummary.rebate || 0) + (playerSummary.rebate || 0), 'cost')}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>總獲利</div>
              <div>{formatWithColor((subAgentSummary.totalProfit || 0) + (playerSummary.totalProfit || 0))}</div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>交上線有效投注</div>
              <div style={{ fontWeight: 'bold' }}>
                {formatCurrency(report?.report?.total?.uplineValidAmount || 0)}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ color: '#999' }}>交上線</div>
              <div>{formatWithColor(report?.report?.total?.uplineAmount || 0)}</div>
            </Col>
          </Row>
        </Card>

        {/* 底部按鈕 */}
        <Row gutter={8} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Button 
              block 
              size="large" 
              style={{ 
                background: '#333', 
                color: '#fff'
              }}
              onClick={loadSubAgentDetails}
              loading={loadingDetails}
            >
              <div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>下級代理明細</div>
              </div>
            </Button>
          </Col>
          <Col span={12}>
            <Button 
              block 
              size="large" 
              style={{ 
                background: '#ff6600',
                borderColor: '#ff6600',
                color: '#fff'
              }}
              onClick={loadPlayerDetails}
              loading={loadingDetails}
            >
              <div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>玩家明細</div>
              </div>
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 下級代理明細 */}
      {showSubAgentDetails && subAgentDetails.length > 0 && (
        <Card size="small" style={{ marginTop: 16 }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>下級代理明細</span>
            <Button 
              size="small" 
              onClick={() => setShowSubAgentDetails(false)}
            >
              關閉
            </Button>
          </div>
          
          <div style={{ overflowX: 'auto' }}>
            {subAgentDetails.map((agent, index) => (
              <div key={agent.key || agent.agentId || `mobile-agent-${index}`} style={{ 
                marginBottom: 12, 
                padding: 12, 
                background: '#fafafa',
                borderRadius: 4 
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  {agent.agentName}
                </div>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>有效投注</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {formatCurrency(agent.validAmount || 0)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>輸贏金額</div>
                    <div>{formatWithColor(agent.winLoss || 0)}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>洗碼佣金</div>
                    <div>{formatWithColor(agent.rebate || 0, 'cost')}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>總獲利</div>
                    <div>{formatWithColor(agent.totalProfit || 0)}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>交上線有效投注</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {formatCurrency(agent.uplineValid || 0)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>交上線</div>
                    <div>{formatWithColor(agent.needToPay || 0)}</div>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 玩家明細 */}
      {showPlayerDetails && playerDetails.length > 0 && (
        <Card size="small" style={{ marginTop: 16 }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>玩家明細</span>
            <Button 
              size="small" 
              onClick={() => setShowPlayerDetails(false)}
            >
              關閉
            </Button>
          </div>
          
          <div style={{ overflowX: 'auto' }}>
            {playerDetails.map((player, index) => (
              <div key={player.key || player.playerId || `mobile-player-${index}`} style={{ 
                marginBottom: 12, 
                padding: 12, 
                background: '#fafafa',
                borderRadius: 4 
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  {player.playerName}
                </div>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>投注金額</div>
                    <div>{formatCurrency(player.betAmount || 0)}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>輸贏金額</div>
                    <div>{formatWithColor(player.winLoss || 0)}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>有效投注</div>
                    <div>{formatCurrency(player.validAmount || 0)}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>洗碼佣金比</div>
                    <div>0%</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#999', fontSize: '12px' }}>洗碼佣金</div>
                    <div>{formatCurrency(0)}</div>
                  </Col>
                  <Col span={24}>
                    <div style={{ color: '#999', fontSize: '12px' }}>總獲利</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {formatWithColor(player.totalProfit || 0)}
                    </div>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  // 桌面端視圖
  const DesktopView = () => (
    <div>
      <Card>
        {/* 頂部標題和資訊 */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>代理輸贏報表</Title>
          </Col>
          <Col>
            <Space>
              <span>{report?.report?.agentInfo?.agentName || 'leo018'}</span>
            </Space>
          </Col>
        </Row>

        {/* 日期選擇區 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={16}>
            <Radio.Group 
              value={quickSelect} 
              onChange={(e) => handleQuickSelect(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="today">今日</Radio.Button>
              <Radio.Button value="yesterday">昨日</Radio.Button>
              <Radio.Button value="thisWeek">本週</Radio.Button>
              <Radio.Button value="lastWeek">上週</Radio.Button>
              <Radio.Button value="thisMonth">本月</Radio.Button>
              <Radio.Button value="lastMonth">上月</Radio.Button>
            </Radio.Group>
          </Col>
          <Col xs={24} sm={8}>
            <Space>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={loadReport}
                style={{ background: '#ff6600', borderColor: '#ff6600' }}
              >
                搜尋
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 下級代理輸贏匯總 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ 
            background: '#fafafa', 
            padding: '12px 16px', 
            marginBottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>下級代理輸贏匯總</span>
          </div>
          
          <Table
            rowKey="key"
            columns={subAgentColumns}
            dataSource={subAgentTableData}
            pagination={false}
            size="middle"
            bordered
          />
        </div>

        {/* 直屬玩家輸贏總合 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ 
            background: '#fafafa', 
            padding: '12px 16px', 
            marginBottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>直屬玩家輸贏總合</span>
          </div>
          
          <Table
            rowKey="key"
            columns={playerColumns}
            dataSource={playerTableData}
            pagination={false}
            size="middle"
            bordered
            scroll={{ x: 'max-content' }}
          />
        </div>

        {/* 總計 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ 
            background: '#fafafa', 
            padding: '12px 16px', 
            marginBottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>總計</span>
          </div>
          
          <Table
            rowKey="key"
            columns={subAgentColumns}
            dataSource={[{
              key: 'total-summary',
              betAmount: (subAgentSummary.betAmount || 0) + (playerSummary.betAmount || 0),
              winLoss: (subAgentSummary.winLoss || 0) + (playerSummary.winLoss || 0),
              validAmount: calculateTotalValidAmount(),
              rebateRate: report?.report?.agentInfo?.rebateRate || 0,
              rebate: (subAgentSummary.rebate || 0) + (playerSummary.rebate || 0),
              totalProfit: (subAgentSummary.totalProfit || 0) + (playerSummary.totalProfit || 0),
              commission: report?.report?.agentInfo?.commissionRate || 0,
              uplineValid: report?.report?.total?.uplineValidAmount || 0,
              upline: report?.report?.total?.uplineAmount || 0
            }]}
            pagination={false}
            size="middle"
            bordered
          />
        </div>

        {/* 底部按鈕 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Button 
              block 
              size="large" 
              style={{ 
                background: '#333', 
                color: '#fff',
                height: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              onClick={loadSubAgentDetails}
              loading={loadingDetails}
            >
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>查看下級代理明細</div>
            </Button>
          </Col>
          <Col span={12}>
            <Button 
              block 
              size="large" 
              style={{ 
                background: '#ff6600',
                borderColor: '#ff6600',
                color: '#fff',
                height: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              onClick={loadPlayerDetails}
              loading={loadingDetails}
            >
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>查看玩家明細</div>
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 下級代理明細 */}
      {showSubAgentDetails && subAgentDetails.length > 0 && (
        <Card style={{ marginTop: 24 }}>
          <div style={{ 
            background: '#fafafa', 
            padding: '12px 16px', 
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>下級代理明細</span>
            <Button 
              size="small" 
              onClick={() => setShowSubAgentDetails(false)}
            >
              關閉
            </Button>
          </div>
          
          <Table
            rowKey="key"
            columns={subAgentDetailColumns}
            dataSource={subAgentDetails}
            pagination={false}
            size="middle"
            bordered
            scroll={{ x: 'max-content' }}
            summary={() => {
              const totalNeedToPay = subAgentDetails.reduce((sum, item) => sum + (item.needToPay || 0), 0);
              const totalBetAmount = subAgentDetails.reduce((sum, item) => sum + (item.betAmount || 0), 0);
              const totalWinLoss = subAgentDetails.reduce((sum, item) => sum + (item.winLoss || 0), 0);
              const totalValidAmount = subAgentDetails.reduce((sum, item) => sum + (item.validAmount || 0), 0);
              const totalRebate = subAgentDetails.reduce((sum, item) => sum + (item.rebate || 0), 0);
              const totalProfit = subAgentDetails.reduce((sum, item) => sum + (item.totalProfit || 0), 0);
              const totalUplineValid = subAgentDetails.reduce((sum, item) => sum + (item.uplineValid || 0), 0);
              
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <strong>總計</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{formatCurrency(totalBetAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{formatWithColor(totalWinLoss)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <strong>{formatCurrency(totalValidAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="center">-</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <strong>{formatWithColor(totalRebate, 'cost')}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <strong>{formatWithColor(totalProfit)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="center">-</Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      <strong>{formatCurrency(totalUplineValid)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right">
                      <strong>{formatWithColor(totalNeedToPay)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}

      {/* 玩家明細 */}
      {showPlayerDetails && playerDetails.length > 0 && (
        <Card style={{ marginTop: 24 }}>
          <div style={{ 
            background: '#fafafa', 
            padding: '12px 16px', 
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>玩家明細</span>
            <Button 
              size="small" 
              onClick={() => setShowPlayerDetails(false)}
            >
              關閉
            </Button>
          </div>
          
          <Table
            rowKey="key"
            columns={playerDetailColumns}
            dataSource={playerDetails}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆`
            }}
            size="middle"
            bordered
            scroll={{ x: 'max-content' }}
            summary={() => {
              const totalBetAmount = playerDetails.reduce((sum, item) => sum + (item.betAmount || 0), 0);
              const totalWinLoss = playerDetails.reduce((sum, item) => sum + (item.winLoss || 0), 0);
              const totalValidAmount = playerDetails.reduce((sum, item) => sum + (item.validAmount || 0), 0);
              const totalProfit = playerDetails.reduce((sum, item) => sum + (item.totalProfit || 0), 0);
              
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <strong>總計</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{formatCurrency(totalBetAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{formatWithColor(totalWinLoss)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <strong>{formatCurrency(totalValidAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="center">0%</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <strong>{formatCurrency(0)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <strong>{formatWithColor(totalProfit)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}
    </div>
  );

  return isMobile ? <MobileView /> : <DesktopView />;
};

export default AgentWinLossReport;
